import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  Typography,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { observer } from "mobx-react-lite";
import { makeStyles, withStyles } from "tss-react/mui";

import { RuleKind } from "@/gql/graphql";

import IntervalItem from "./IntervalItem";
import { useSchedule } from "../SchedulerProvider";
import Interval from "../store/Interval";
import Rule from "../store/Rule";
import Schedule from "../store/Schedule";

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
    marginTop: 8,
    marginBottom: 14,
  },
  icon: {
    color: "#7D8797",
    height: 20,
    width: 20,
  },
}));

type ScheduleListItemProps = {
  rule: Rule;
  wday?: string | null;
};

const StyledMenu = withStyles(Menu, {
  root: {
    "& .MuiPaper-root": {
      padding: 14,
    },
  },
});

const StyledButton = withStyles(Button, {
  root: {
    width: "100%",
  },
});

type CopyMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  handleClose: () => void;
  rule: Rule;
  handleCheckRule: () => void;
  schedule: Schedule;
};

const CopyMenu: React.FC<CopyMenuProps> = ({
  anchorEl,
  open,
  handleClose,
  rule,
  handleCheckRule,
  schedule,
}) => {
  const [recipients, setRecipients] = useState<Rule[]>([]);
  function handleCheck(rule: Rule) {
    setRecipients((prevIndex) => [...prevIndex, rule]);
  }

  function handleUnCheck(rule: Rule) {
    const newIndexes = recipients.filter((i) => i !== rule);
    setRecipients(newIndexes);
  }

  function handleCopy() {
    schedule.copyIntervals(rule, recipients);
    handleClose();
    setRecipients([]);
    handleCheckRule();
  }

  return (
    <StyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      sx={{ minWidth: "182px" }}
    >
      <ListItemText>Copy times to...</ListItemText>
      {schedule.wdayRules.map((rule: Rule, index: number) => {
        if (rule.ruleType === RuleKind.Wday) {
          return (
            <MenuItem key={rule.wday}>
              <Checkbox
                onChange={(event) => {
                  if (event.target.checked) {
                    handleCheck(rule);
                  } else {
                    handleUnCheck(rule);
                  }
                }}
              />
              <ListItemText>{rule.wday}</ListItemText>
            </MenuItem>
          );
        }
      })}
      <StyledButton onClick={handleCopy}>Apply</StyledButton>
    </StyledMenu>
  );
};

const ScheduleListItem: React.FC<ScheduleListItemProps> = observer(
  ({ wday, rule }) => {
    const schedule = useSchedule();
    const { classes } = useStyles();
    const day = wday?.substring(0, 3).toUpperCase();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpenCopyMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleCloseCopyMenu = () => {
      setAnchorEl(null);
    };

    function renderIntervals() {
      if (rule.presentIntervals.length > 0) {
        if (rule.checked) {
          return rule.presentIntervals.map(
            (e: Interval, intervalIndex: number) => {
              return (
                <IntervalItem key={intervalIndex} interval={e} rule={rule} />
              );
            }
          );
        } else {
          return <Typography variant="h6">Unavailable</Typography>;
        }
      } else {
        return <Typography variant="h6">No Schedule set</Typography>;
      }
    }

    function handleRemoveAllInterval() {
      rule.removeIntervals();
    }

    function handleCheckRule() {
      rule.restoreIntervals();
    }

    const actionIcons = (
      <>
        <IconButton
          sx={{ marginRight: { xs: 1, md: 8 } }}
          onClick={() => {
            rule.addInterval();
          }}
        >
          <AddIcon className={classes.icon} />
        </IconButton>
        <IconButton onClick={handleOpenCopyMenu}>
          <ContentCopyIcon className={classes.icon} />
        </IconButton>
      </>
    );

    return (
      <div className={classes.root}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          gap={{ xs: 1, md: 0 }}
          marginBottom={6}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={rule.checked}
                onChange={(value) => {
                  if (!value.target.checked) {
                    handleRemoveAllInterval();
                  } else {
                    handleCheckRule();
                  }
                }}
              />
              <Typography variant="body1" fontWeight="bold">
                {day}
              </Typography>
            </Box>
            <Box sx={{ display: { xs: "flex", md: "none" } }}>
              {actionIcons}
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            marginLeft={{ xs: 0, md: 2 }}
          >
            {renderIntervals()}
          </Box>
          <Box marginLeft={12} sx={{ display: { xs: "none", md: "block" } }}>
            {actionIcons}
          </Box>
        </Box>
        <Divider light />
        <CopyMenu
          anchorEl={anchorEl}
          open={open}
          handleClose={handleCloseCopyMenu}
          rule={rule}
          handleCheckRule={handleCheckRule}
          schedule={schedule}
        />
      </div>
    );
  }
);

export default ScheduleListItem;
